import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Our components
import NorowsData from './NorowsData';
import Notification from './Notification';
import OrderStatus from './OrderStatus';
import Customer from './Customer';
import CarrierTracking from './CarrierTracking';
import Date from './Date';
import WidthMaximize from './WidthMaximize';
import TrackingDrawer from '../../pages/General/TrackingDrawer';
import OrderDrawer from '../General/OrderDrawer';
import Printer from '../General/Printer'; // Imported Printer context module

// Mui components
import {
    FormControl,
    Pagination,
    Card,
    IconButton,
    Drawer,
    CardHeader,
    LinearProgress,
    Select,
    MenuItem,
    Typography,
    Stack,
    Button
} from "@mui/material";
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import Box from '@mui/material/Box';

// Redux actions
import { fetchOrderDetail } from '../../pages/OrderDetail/orderdetailsSlice';
import { fetchCustomerDetails } from '../Reports/Deliveries/trackingEventsSlice';
import { 
    fetchBulkOrderShipments, 
    resetBulkOrderNotifications 
} from '../../pages/BulkOrder/bulkOrderDetailsSlice';
import { printBulkLabels } from '../../pages/Orders/ordersSlice';
import { clearFulfillmentPrintDetails } from '../../pages/Reports/Fulfillments/fulfillmentsLogSlice'; // Adjusted cross-slice path reference if applicable

const enterpriseDrawerControlStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const BulkOrderDetailsDrawer = ({ visible, bulkReferenceId, type, onClose }) => {
    const dispatch = useDispatch();
    const bulkState = useSelector(state => state.bulkOrderDetails);
    const profile = useSelector(state => state.auth);
    const currentUserId = profile?.profile?.id || 4465;

    // Local states for controlling the shared binary printing layout engine
    const [printUrl, setPrintUrl] = useState("");
    const [isPrinted, setIsPrinted] = useState(true);
    const [isPrintingLabels, setIsPrintingLabels] = useState(false);
    const [meta, setMeta] = useState({ page: 1, per_page: 10, sorting: JSON.stringify([{ field: 'order_date', sort: 'desc' }]) });
    const [trackingDrawerVisible, setTrackingDrawerVisible] = useState(false);
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [isMultiPiece, setIsMultiPiece] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState("80%");

    const handleDrawerClose = () => {
        dispatch(resetBulkOrderNotifications());
        onClose(false);
        setMeta({ page: 1, per_page: 10, sorting: JSON.stringify([{ field: 'order_date', sort: 'desc' }])});
    };

    const getDrawerTitle = () => {
        switch(type) {
            case 'packed': return `Bulk Packed Shipments - ${bulkReferenceId}`;
            case 'not_packed': return `Bulk Not Packed Shipments - ${bulkReferenceId}`;
            default: return `Bulk Successful Shipments - ${bulkReferenceId}`;
        }
    };

    useEffect(() => {
        if (visible && bulkReferenceId) {
            dispatch(fetchBulkOrderShipments({
                userid: currentUserId,
                bulk_reference_id: bulkReferenceId,
                type: type,
                page: meta.page,
                per_page: meta.per_page,
                sorting: meta.sorting
            }));
        }
    }, [dispatch, visible, bulkReferenceId, type, meta.page, meta.per_page, meta.sorting]);

    const handlePrintLabelsClick = () => {
        const rowsData = bulkState?.shipmentData?.data || [];
        if (rowsData.length === 0) return;

        // Turn on the loading spinner overlay
        setIsPrintingLabels(true);

        // Put processing block in a minimal timeout to let UI update cleanly
        setTimeout(() => {
            try {
                let labels = [];

                rowsData.forEach((shipmentData) => {
                    const url = shipmentData?.carrier_label_url || 
                                shipmentData?.label_url || 
                                shipmentData?.direct_label_url ||
                                shipmentData?.detachable_label_url ||
                                shipmentData?.detachable_direct_label_url;

                    const isVoided = shipmentData?.is_voided === 1 || 
                                     shipmentData?.is_voided === true || 
                                     shipmentData?.shipment_status_category === "Voided";

                    if (url && url !== "" && !isVoided) {
                        if (!labels.includes(url)) {
                            labels.push(url);
                        }
                    }
                });

                if (labels.length === 1) {
                    setPrintUrl(labels[0]); 
                    setIsPrinted(false); 
                } else if (labels.length > 1) {
                    dispatch(printBulkLabels(labels, currentUserId));
                }
            } catch (err) {
                console.error("Label compilation failed", err);
            } finally {
                // Turn off the loading state
                setIsPrintingLabels(false);
            }
        }, 300);
    };

    const orderCallback = (item) => {
        dispatch(fetchOrderDetail(item));
        setOrderDrawerVisible(true);
    };

    const trackingCallback = (carriername, trackingNumber) => {
        let apikey = (profile && profile.profile && profile.profile.api_token) ? profile.profile.api_token : "";
        const data = { 'tracking_number': trackingNumber };
        if (trackingNumber.charAt(0) === 'M') {
            setIsMultiPiece(true);
        }
        dispatch(fetchCustomerDetails(data, apikey));
        setTrackingDrawerVisible(true);
    };

    const onSortChange = (currentSorting) => {
        setMeta({ ...meta, sorting: JSON.stringify(currentSorting) });
    };

    const onChangePerPage = (event) => {
        setMeta({ ...meta, page: 1, per_page: event.target.value });
    };

    const onChangePagination = (event, page) => {
        setMeta({ ...meta, page: page });
    };

    const orderOnClose = (values) => {
        setOrderDrawerVisible(false);
    };

    const columns = [
        {
            field: 'order_id',
            headerName: 'Order ID',
            headerClassName: 'super-app-theme--header',
            minWidth: 150,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography 
                        variant="body2" 
                        onClick={e => { orderCallback(params.row.order_id) }}
                        sx={{ color: '#1976d2', cursor: 'pointer', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', height: '100%', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {params.row.order_id}
                    </Typography>
                );
            }
        },
        {
            field: 'tracking_number',
            headerName: 'Tracking Number',
            minWidth: 220,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                let tracking_number = params.row.tracking_number || "";
                let carrier_name = params.row.carrier_name || "";
                let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name };
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <CarrierTracking trackingItems={tracking} dispatchCallback={trackingCallback} carrierImageWidth="60px" />
                    </Box>
                );
            }
        },
        {
            field: 'shipment_status_category',
            headerName: 'Shipment Status',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <OrderStatus orderStatus={params.row.shipment_status_category} />
                </Box>
            )
        },
        {
            field: 'customer_name',
            headerName: 'Customer',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Customer name={params.row.customer_name} />
                    </Box>
                )
            }
        },
        {
            field: 'order_date',
            headerName: 'Order Date',
            minWidth: 160,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Date date={params.row.order_date} />
                </Box>
            )
        }
    ];

    function CustomTotalRows() {
        const dataLength = bulkState?.shipmentData?.data?.length || 0;
        const totalRows = bulkState?.shipmentData?.meta?.total || 0;
        return (
            <Box className="custom-total-rows" sx={{ display: 'inline-flex', alignItems: 'center', py: 0.6, px: 1.5, borderRadius: '8px', backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0' }}>
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}>
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{dataLength}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{totalRows}</strong>
                </Typography>
            </Box>
        );
    }

    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseDrawerControlStyles }}>
                        <Select value={meta?.per_page || 10} onChange={onChangePerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, width: '100%', boxSizing: 'border-box' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        return (
            <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', boxSizing: 'border-box', '& .MuiPaginationItem-root': { fontSize: '13px', fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: '#1976d2', color: '#FFFFFF', '&:hover': { backgroundColor: '#115293' } } } }}>
                <Pagination
                    color="primary"
                    shape="circular"
                    page={bulkState?.shipmentData?.meta?.current_page || 1}
                    count={bulkState?.shipmentData?.meta?.last_page || 1}
                    onChange={onChangePagination}
                />
            </Box>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={(event, reason) => { if (reason !== "backdropClick") handleDrawerClose(); }}
            PaperProps={{ sx: { width: drawerWidth, backgroundColor: '#F8FAFC', borderLeft: '1px solid #E2E8F0' } }}
        >
            {bulkState?.shipmentData?.notification?.show &&
                <Notification
                    is_open={bulkState.shipmentData.notification.show}
                    messageType={bulkState.shipmentData.notification.messageType}
                    messages={bulkState.shipmentData.notification.messageContent}
                    handleClose={() => dispatch(resetBulkOrderNotifications())}
                />}
            <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
                <Card sx={{ width: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
                    <Box sx={{ width: '100%', background: '#fff' }}>
                        <CardHeader 
                            title={
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                    {getDrawerTitle()}
                                </Typography>
                            }
                            action={
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    {/* ─── CONDITIONAL PRINT LABEL HEADER ACTION BUTTON INJECTED HERE ─── */}
                                    {type === 'not_packed' && (bulkState?.shipmentData?.data || []).length > 0 && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<LocalPrintshopIcon />}
                                            onClick={handlePrintLabelsClick}
                                            
                                            // ─── DISABLE CLICKS IF PARENT IS LOADING OR IF LOCAL PRINT ENGINE RUNS ───
                                            disabled={bulkState?.shipmentData?.loading || isPrintingLabels}
                                            
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                height: '32px',
                                                px: 1.8,
                                                backgroundColor: '#1976d2',
                                                '&:hover': { backgroundColor: '#115293' }
                                            }}
                                        >
                                            {isPrintingLabels ? 'Processing...' : 'Print Labels'}
                                        </Button>
                                    )}

                                    <WidthMaximize defaultWidth="80%" onToggle={setDrawerWidth} />
                                    <IconButton onClick={handleDrawerClose} sx={{ color: '#64748B', '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' } }}>
                                        <CloseIcon sx={{ fontSize: '20px' }} />
                                    </IconButton>
                                </Stack>
                            } 
                            sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}
                        />
                        <DataGrid
                            sx={{
                                border: 'none',
                                backgroundColor: '#FFFFFF',
                                '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC' },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
                                '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px' },
                                '& .MuiDataGrid-row': { borderBottom: '1px solid #F1F5F9', '&:hover': { backgroundColor: '#F8FAFC' } },
                                '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', py: '12px' }
                            }}
                            getRowId={(row) => row.id || row.order_id}
                            rows={bulkState?.shipmentData?.data || []}
                            columns={columns}
                            autoHeight
                            disableColumnFilter
                            disableColumnMenu
                            sortingMode="server"
                            onSortModelChange={onSortChange}
                            sortModel={meta?.sorting ? JSON.parse(meta.sorting) : []}
                            disableRowSelectionOnClick
                            slots={{
                                loadingOverlay: LinearProgress,
                                footer: CustomPagination,
                                toolbar: CustomToolbar,
                                NorowsData
                            }}
                            loading={bulkState?.shipmentData?.loading}
                        />
                    </Box>
                </Card>
            </Box>
            
            {/* ─── PRINT WRAPPER ATTACHMENT NODE FOR EMITTING RAW TARGET PDF STREAM STRINGS ─── */}
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />

            <TrackingDrawer visible={trackingDrawerVisible} isMultiPiece={isMultiPiece} onClose={() => setTrackingDrawerVisible(false)} />
            <OrderDrawer visible={orderDrawerVisible} onClose={orderOnClose} />
        </Drawer>
    );
};

export default BulkOrderDetailsDrawer;